import { createSlice } from '@reduxjs/toolkit'
import pdf_configs from '../../configs/pdf_configs'

const initialState = {
    // Core costs
    systemCost: 10000,
    netCost: 0,
    vat: pdf_configs?.vat_rate || 0,
    holdBasePrice: 0,

    // Finance options
    financeOption: null,
    finance: {},
    financeTerm: 0,
    financeCharge: 0,
    finMonthlyPayment: 0,
    finTotalCost: 0,

    // Deposit
    depositAmount: 0,

    // Discounts & Commission
    additionalDiscount: 0,
    sharedDiscount: 0,
    commPerc: 8, // Default commission percentage
    commSacrifice: 0, // Rep sacrificed commission for offers

    // Maintenance
    selectedMaintenance: null,

    // Ancillary costs (from API)
    ancillaryItems: [],

    // Offer
    offerId: '7 Day Offer 5',

    // Flags
    dontUpdateBase: false,
    noRateDisclaimer: false
}

const financialSlice = createSlice({
    name: 'financial',
    initialState,
    reducers: {
        setSystemCost: (state, action) => {
            state.systemCost = action.payload
        },
        setNetCost: (state, action) => {
            state.netCost = action.payload
        },
        setVat: (state, action) => {
            state.vat = action.payload
        },
        setHoldBasePrice: (state, action) => {
            state.holdBasePrice = action.payload
        },
        setFinanceOption: (state, action) => {
            state.financeOption = action.payload
        },
        setFinance: (state, action) => {
            state.finance = action.payload
        },
        setFinanceTerm: (state, action) => {
            state.financeTerm = action.payload
        },
        setFinanceCharge: (state, action) => {
            state.financeCharge = action.payload
        },
        setFinMonthlyPayment: (state, action) => {
            state.finMonthlyPayment = action.payload
        },
        setFinTotalCost: (state, action) => {
            state.finTotalCost = action.payload
        },
        setDepositAmount: (state, action) => {
            state.depositAmount = action.payload
        },
        setAdditionalDiscount: (state, action) => {
            state.additionalDiscount = action.payload
        },
        setSharedDiscount: (state, action) => {
            state.sharedDiscount = action.payload
        },
        setCommPerc: (state, action) => {
            state.commPerc = action.payload
        },
        setCommSacrifice: (state, action) => {
            state.commSacrifice = action.payload
        },
        setSelectedMaintenance: (state, action) => {
            state.selectedMaintenance = action.payload
        },
        setAncillaryItems: (state, action) => {
            state.ancillaryItems = action.payload
        },
        setOfferId: (state, action) => {
            state.offerId = action.payload
        },
        setDontUpdateBase: (state, action) => {
            state.dontUpdateBase = action.payload
        },
        setNoRateDisclaimer: (state, action) => {
            state.noRateDisclaimer = action.payload
        },

        // Bulk update from quote data
        updateFinancialFromQuote: (state, action) => {
            const data = action.payload
            if (data.systemCost !== undefined) state.systemCost = data.systemCost
            if (data.systemcost !== undefined) state.systemCost = data.systemcost
            if (data.netCost !== undefined) state.netCost = data.netCost
            if (data.net_cost !== undefined) state.netCost = data.net_cost
            if (data.vat !== undefined) state.vat = data.vat
            if (data.holdBasePrice !== undefined) state.holdBasePrice = data.holdBasePrice
            if (data.hold_base_price !== undefined) state.holdBasePrice = data.hold_base_price
            if (data.financeOption !== undefined) state.financeOption = data.financeOption
            if (data.financeoption !== undefined) state.financeOption = data.financeoption
            if (data.finance !== undefined) state.finance = data.finance
            if (data.financeTerm !== undefined) state.financeTerm = data.financeTerm
            if (data.financeterm !== undefined) state.financeTerm = data.financeterm
            if (data.financeCharge !== undefined) state.financeCharge = data.financeCharge
            if (data.finance_charge !== undefined) state.financeCharge = data.finance_charge
            if (data.finMonthlyPayment !== undefined) state.finMonthlyPayment = data.finMonthlyPayment
            if (data.fin_monthlypayment !== undefined) state.finMonthlyPayment = data.fin_monthlypayment
            if (data.finTotalCost !== undefined) state.finTotalCost = data.finTotalCost
            if (data.fin_totalcost !== undefined) state.finTotalCost = data.fin_totalcost
            if (data.depositAmount !== undefined) state.depositAmount = data.depositAmount
            if (data.deposit_amount !== undefined) state.depositAmount = data.deposit_amount
            if (data.additionalDiscount !== undefined) state.additionalDiscount = data.additionalDiscount
            if (data.additional_discount !== undefined) state.additionalDiscount = data.additional_discount
            if (data.sharedDiscount !== undefined) state.sharedDiscount = data.sharedDiscount
            if (data.shared_discount !== undefined) state.sharedDiscount = data.shared_discount
            if (data.commPerc !== undefined) state.commPerc = data.commPerc
            if (data.commperc !== undefined) state.commPerc = data.commperc
            if (data.commSacrifice !== undefined) state.commSacrifice = data.commSacrifice
            if (data.comm_sacrifice !== undefined) state.commSacrifice = data.comm_sacrifice
            if (data.ancillaryItems !== undefined) state.ancillaryItems = data.ancillaryItems
            if (data.ancillary_items !== undefined) state.ancillaryItems = data.ancillary_items
            if (data.offerId !== undefined) state.offerId = data.offerId
            if (data.offerid !== undefined) state.offerId = data.offerid
            if (data.dontUpdateBase !== undefined) state.dontUpdateBase = data.dontUpdateBase
            if (data.dont_update_base !== undefined) state.dontUpdateBase = data.dont_update_base
            if (data.noRateDisclaimer !== undefined) state.noRateDisclaimer = data.noRateDisclaimer
            if (data.noratedisclaimer !== undefined) state.noRateDisclaimer = data.noratedisclaimer
        },

        resetFinancial: () => initialState
    }
})

export const {
    setSystemCost,
    setNetCost,
    setVat,
    setHoldBasePrice,
    setFinanceOption,
    setFinance,
    setFinanceTerm,
    setFinanceCharge,
    setFinMonthlyPayment,
    setFinTotalCost,
    setDepositAmount,
    setAdditionalDiscount,
    setSharedDiscount,
    setCommPerc,
    setCommSacrifice,
    setSelectedMaintenance,
    setAncillaryItems,
    setOfferId,
    setDontUpdateBase,
    setNoRateDisclaimer,
    updateFinancialFromQuote,
    resetFinancial
} = financialSlice.actions

// Selectors
export const selectSystemCost = (state) => state.financial.systemCost
export const selectNetCost = (state) => state.financial.netCost
export const selectVat = (state) => state.financial.vat
export const selectHoldBasePrice = (state) => state.financial.holdBasePrice
export const selectFinanceOption = (state) => state.financial.financeOption
export const selectFinance = (state) => state.financial.finance
export const selectFinanceTerm = (state) => state.financial.financeTerm
export const selectFinanceCharge = (state) => state.financial.financeCharge
export const selectFinMonthlyPayment = (state) => state.financial.finMonthlyPayment
export const selectFinTotalCost = (state) => state.financial.finTotalCost
export const selectDepositAmount = (state) => state.financial.depositAmount
export const selectAdditionalDiscount = (state) => state.financial.additionalDiscount
export const selectSharedDiscount = (state) => state.financial.sharedDiscount
export const selectCommPerc = (state) => state.financial.commPerc
export const selectCommSacrifice = (state) => state.financial.commSacrifice
export const selectSelectedMaintenance = (state) => state.financial.selectedMaintenance
export const selectAncillaryItems = (state) => state.financial.ancillaryItems
export const selectOfferId = (state) => state.financial.offerId
export const selectDontUpdateBase = (state) => state.financial.dontUpdateBase
export const selectNoRateDisclaimer = (state) => state.financial.noRateDisclaimer

// Computed selectors
export const selectTotalDiscount = (state) =>
    state.financial.additionalDiscount + state.financial.sharedDiscount

export const selectVatAmount = (state) =>
    state.financial.systemCost * (state.financial.vat / 100)

export const selectPriceWithVat = (state) =>
    state.financial.systemCost * (1 + state.financial.vat / 100)

export default financialSlice.reducer
